import React from 'react';
import questionnaire from '../../config/questionnaire';
import { formatNavieraDiagnostics } from '../ui/NavieraDiagnostics';
import { LIKERT_MOODS } from '../ui/LikertScale';

const PreviewStep = ({ formData }) => {
  const dimensions = questionnaire.getDimensions();

  const formatPreviewValue = (question, rawValue) => {
    if (!rawValue) return null;
    if (question.type === 'naviera_diagnostics') return formatNavieraDiagnostics(rawValue);
    if (question.type !== 'likert') return rawValue;
    const numericValue = parseInt(rawValue, 10);
    const likertMatch = LIKERT_MOODS.find((mood) => mood.value === numericValue);
    return likertMatch ? likertMatch.label : rawValue;
  };

  const getMoodIcon = (question, rawValue) => {
    if (question.type !== 'likert' || !rawValue) return null;
    const numericValue = parseInt(rawValue, 10);
    const likertMatch = LIKERT_MOODS.find((mood) => mood.value === numericValue);
    if (!likertMatch) return null;
    const MoodIcon = likertMatch.icon;
    return <MoodIcon className={`h-5 w-5 ${likertMatch.accent}`} />;
  };

  return (
    <div className="space-y-5">
      <div>
        <p className="text-[0.6rem] uppercase text-slate-400">Revisión final</p>
        <h2 className="text-xl font-bold font-jakarta text-slate-900 mt-0.5">Confirma tus respuestas</h2>
        <p className="text-sm text-slate-500 mt-1">Verifica cada pregunta antes de enviar. Puedes volver atrás para corregir.</p>
      </div>
      <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1" style={{ scrollbarWidth: 'thin', scrollbarColor: '#CBD5E1 transparent' }}>
        {dimensions.map((dimension) => {
          const answeredCount = dimension.questions.filter(q => formData[q.id] && formData[q.id] !== '').length;
          const totalCount = dimension.questions.length;
          const allAnswered = answeredCount === totalCount;
          return (
            <div key={dimension.id} className="rounded-2xl border border-slate-200/70 overflow-hidden">
              {/* Dimension header */}
              <div className="px-4 py-3 bg-gradient-to-r from-brand-blue/[0.06] to-transparent border-b border-slate-100 flex items-center justify-between gap-3">
                <div>
                  <p className="text-[0.6rem] uppercase text-slate-400">{dimension.id}</p>
                  <p className="text-sm font-bold text-slate-900">{dimension.name}</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-[0.65rem] font-bold ${
                  allAnswered
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
                    : 'bg-amber-100 text-amber-700 border border-amber-200'
                }`}>
                  {allAnswered ? `✓ ${answeredCount}/${totalCount}` : `⚠ ${answeredCount}/${totalCount}`}
                </span>
              </div>
              {/* Questions */}
              <div className="divide-y divide-slate-100">
                {dimension.questions.map(q => {
                  const rawValue = formData[q.id];
                  const displayValue = formatPreviewValue(q, rawValue);
                  const hasValue = !!rawValue && rawValue !== '';
                  const moodIcon = getMoodIcon(q, rawValue);
                  return (
                    <div key={q.id} className="px-4 py-3 flex items-start gap-3">
                      <span className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full text-[0.6rem] font-bold flex-shrink-0 ${
                        hasValue ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'
                      }`}>
                        {hasValue ? '✓' : '–'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 leading-snug">{q.text}</p>
                        <div className="mt-1 flex items-center gap-2">
                          {moodIcon}
                          <p className={`text-sm font-semibold whitespace-pre-line ${hasValue ? 'text-brand-blue' : 'text-slate-400 italic'}`}>
                            {displayValue || '(Sin respuesta)'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PreviewStep;
